import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { Link } from 'react-router-dom';

const BASE_URL = import.meta.env.VITE_SITE_URL ?? 'https://roameurope.blog';

const schemaJson = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Terms of Service — roameurope.blog',
  description: 'Read the Terms of Service for roameurope.blog. By using this site you agree to these terms.',
  url: `${BASE_URL}/terms`,
  dateModified: '2026-04-09',
  publisher: {
    '@type': 'Organization',
    name: 'roameurope.blog',
    url: BASE_URL,
  },
  inLanguage: 'en-US',
});

export default function TermsPage() {
  useSeoMeta({
    title: 'Terms of Service — roameurope.blog',
    description: 'Read the Terms of Service for roameurope.blog. By using this site you agree to these terms.',
    url: '/terms',
    type: 'website',
    noindex: true,
  });

  const lastUpdated = 'April 9, 2026';

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schemaJson }}
      />
      <Navbar />
      <main className="pt-28 pb-20 min-h-screen bg-white dark:bg-stone-950 transition-colors duration-300">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h1 className="text-4xl font-bold text-stone-900 dark:text-stone-50 mb-2" style={{ fontFamily: "'Lora', serif" }}>
            Terms of Service
          </h1>
          <p className="text-xs text-stone-400 dark:text-stone-500 mb-10">Last updated: {lastUpdated}</p>

          <div className="space-y-8 text-stone-600 dark:text-stone-400 leading-relaxed text-sm">
            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">1. Acceptance of Terms</h2>
              <p>By accessing or using roameurope.blog (the &ldquo;Site&rdquo;), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Site.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">2. Use of Content</h2>
              <p>All content published on roameurope.blog — including articles, images, and other materials — is for informational and educational purposes only. You may not reproduce, distribute, or republish any content from this Site without prior written permission.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">3. No Professional Advice</h2>
              <p>The content on this Site is not a substitute for professional psychological, therapeutic, medical, or legal advice. Always seek the guidance of a qualified professional with any questions you may have regarding your mental health, relationships, or personal circumstances. Never disregard professional advice or delay seeking it because of something you have read on this Site.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">4. Intellectual Property</h2>
              <p>All content, trademarks, logos, and intellectual property on this Site are owned by or licensed to roameurope.blog. Unauthorized use is strictly prohibited.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">5. Third-Party Links &amp; Advertising</h2>
              <p>This Site may contain links to third-party websites and display advertisements served by Google AdSense and other networks. We are not responsible for the content, accuracy, or practices of any third-party sites. The presence of an advertisement does not constitute an endorsement.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">6. Limitation of Liability</h2>
              <p>roameurope.blog and its authors shall not be liable for any direct, indirect, incidental, or consequential damages arising from your use of, or inability to use, this Site or its content. Your use of the Site is at your sole risk.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">7. User Conduct</h2>
              <p>You agree not to use this Site for any unlawful purpose, to transmit harmful or offensive content, to attempt to gain unauthorized access to any part of the Site, or to interfere with the Site&apos;s operation.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">8. Disclaimer of Warranties</h2>
              <p>This Site is provided &ldquo;as is&rdquo; without warranties of any kind, either express or implied. We do not warrant that the Site will be uninterrupted, error-free, or free of viruses or other harmful components.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">9. Changes to Terms</h2>
              <p>We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting. Your continued use of the Site after any changes constitutes your acceptance of the new terms.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">10. Governing Law</h2>
              <p>These Terms shall be governed by and construed in accordance with applicable law. Any disputes arising from these Terms or your use of the Site shall be subject to the exclusive jurisdiction of the relevant courts.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">11. Contact</h2>
              <p>If you have questions about these Terms, please <Link to="/contact" className="text-rose-500 hover:underline">contact us</Link>.</p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
